import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { GraduationCapIcon, ShieldCheckIcon, MailIcon, ArrowRightIcon, CheckIcon, LockIcon, ZapIcon, ClipboardListIcon, SparklesIcon, SchoolIcon, StarIcon, AwardIcon } from 'lucide-react';
import { getBridgeTemplate } from '../data/bridgeTemplates';
import { useMetaTags } from '../hooks/useMetaTags';
const NAVY = '#1a2332';
const NAVY_ACCENT = '#2a374a';
const ORANGE = '#e8594f';
const ORANGE_HOVER = '#d64a41';
export function FindDegreesBridgePage() {
  const {
    templateId
  } = useParams<{
    templateId: string;
  }>();
  const template = getBridgeTemplate(templateId);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useMetaTags({
    title: `${template.headlineHighlight} | FindOnlineDegrees`,
    description: template.subhead
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('email', email);
    navigate(`/?${params.toString()}`);
  };
  const handleCtaClick = () => {
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    navigate(`/?${params.toString()}`);
  };
  const isCta = template.variant === 'cta';
  return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-100" data-id="element-933">
      {/* Slim Header — matches site nav style */}
      <header className="w-full relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-934">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-935"></div>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-936">
          <div className="flex items-center gap-2.5" data-id="element-937">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
            backgroundColor: ORANGE
          }} data-id="element-938">
              <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-939" />
            </div>
            <div className="leading-tight" data-id="element-940">
              <div className="font-bold text-white text-base" data-id="element-941">
                FindOnlineDegrees
              </div>
              <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-942">
                FREE DEGREE MATCHING
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-943">
            <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-944" />
            <span data-id="element-945">SSL Secured</span>
          </div>
        </div>
      </header>

      {/* Main Content — Two-Column, vertically centered */}
      <section className="flex-grow flex items-center" data-id="element-946">
        <div className="w-full max-w-7xl mx-auto px-4 py-10 md:py-16 lg:py-20" data-id="element-947">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-948">
            {/* LEFT: Headline */}
            <div className="text-center lg:text-left" data-id="element-949">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-5 text-[11px] font-bold tracking-widest" style={{
              backgroundColor: 'rgba(232,89,79,0.1)',
              color: ORANGE
            }} data-id="element-950">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: ORANGE
              }} data-id="element-951"></span>
                {template.eyebrow}
              </div>

              {/* Headline */}
              <h1 className="font-bold leading-[1.1] mb-5 text-gray-900" style={{
              fontSize: 'clamp(2rem, 4.5vw, 3.25rem)'
            }} data-id="element-952">
                {template.headlineLead}{' '}
                <span style={{
                color: ORANGE
              }} data-id="element-953">
                  {template.headlineHighlight}
                </span>{' '}
                {template.headlineTrail}
              </h1>

              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0" data-id="element-954">
                {template.subhead}
              </p>

              {/* Stats strip — site theme */}
              <div className="rounded-xl p-5 md:p-6 grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0 relative overflow-hidden" style={{
              backgroundColor: NAVY
            }} data-id="element-955">
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-956"></div>
                <div className="text-center relative z-10" data-id="element-957">
                  <div className="text-2xl md:text-3xl font-bold" style={{
                  color: ORANGE
                }} data-id="element-958">
                    200+
                  </div>
                  <div className="text-[11px] text-gray-300 mt-1" data-id="element-959">
                    Accredited Schools
                  </div>
                </div>
                <div className="text-center border-x border-white/10 relative z-10" data-id="element-960">
                  <div className="text-2xl md:text-3xl font-bold" style={{
                  color: ORANGE
                }} data-id="element-961">
                    1,400+
                  </div>
                  <div className="text-[11px] text-gray-300 mt-1" data-id="element-962">
                    Online Programs
                  </div>
                </div>
                <div className="text-center relative z-10" data-id="element-963">
                  <div className="text-2xl md:text-3xl font-bold" style={{
                  color: ORANGE
                }} data-id="element-964">
                    100%
                  </div>
                  <div className="text-[11px] text-gray-300 mt-1" data-id="element-965">
                    Free Matching
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Form — better aligned, cleaner spacing */}
            <aside className="w-full max-w-md mx-auto lg:max-w-none lg:mx-0" data-id="element-966">
              {/* Step indicator */}
              <div className="flex items-center gap-3 mb-5 px-1" data-id="element-967">
                <div className="flex items-center gap-2" data-id="element-968">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{
                  backgroundColor: ORANGE
                }} data-id="element-969">
                    <CheckIcon className="w-4 h-4" data-id="element-970" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700" data-id="element-971">
                    Profile Complete
                  </span>
                </div>
                <div className="flex-grow h-px bg-gray-300" data-id="element-972"></div>
                <div className="flex items-center gap-2" data-id="element-973">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse" style={{
                  backgroundColor: NAVY,
                  boxShadow: `0 0 0 4px rgba(232,89,79,0.15)`
                }} data-id="element-974">
                    2
                  </div>
                  <span className="text-xs font-semibold" style={{
                  color: NAVY
                }} data-id="element-975">
                    Confirm Email
                  </span>
                </div>
              </div>

              {isCta ? <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-gray-200 text-center" data-id="element-976">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-977">
                    {template.cardHeadline || template.cardTitle}
                  </h2>
                  <p className="text-base font-semibold mb-7" style={{
                color: ORANGE
              }} data-id="element-978">
                    {template.cardSubcopy || template.cardSubtitle}
                  </p>

                  <button type="button" onClick={handleCtaClick} disabled={submitting} className="inline-flex items-center justify-center gap-2 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-979">
                    <span data-id="element-980">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-981" />
                  </button>

                  {template.cardFootnote && <p className="text-sm text-gray-600 mt-7 leading-relaxed max-w-sm mx-auto" data-id="element-982">
                      {template.cardFootnote}
                    </p>}
                </div> : <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-983">
                  <label htmlFor="bridge-email" className="block text-sm font-bold mb-3 text-gray-900" data-id="element-984">
                    Where should we send your matches?
                  </label>

                  <div className="relative mb-4" data-id="element-985">
                    <MailIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-986" />
                    <input id="bridge-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="yourname@example.com" className="w-full pl-12 pr-4 py-3.5 rounded-lg border-2 border-gray-200 bg-white text-base text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors" data-id="element-987" />
                  </div>

                  <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-988">
                    <span data-id="element-989">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-990" />
                  </button>

                  {/* Inline trust row */}
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5 text-xs text-gray-600" data-id="element-991">
                    <div className="flex items-center gap-1.5" data-id="element-992">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-993" />
                      <span data-id="element-994">No credit card</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-995">
                      <ZapIcon className="w-3.5 h-3.5 text-green-500" data-id="element-996" />
                      <span data-id="element-997">Instant results</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-998">
                      <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-999" />
                      <span data-id="element-1000">100% free</span>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 mt-5 leading-relaxed text-center" data-id="element-1001">
                    By clicking, you agree to our{' '}
                    <a href="/terms" className="underline text-gray-700 hover:text-gray-900" data-id="element-1002">
                      Terms
                    </a>{' '}
                    &{' '}
                    <a href="/privacy-policy" className="underline text-gray-700 hover:text-gray-900" data-id="element-1003">
                      Privacy Policy
                    </a>{' '}
                    and consent to receive program info via email, phone, or
                    text.
                  </p>
                </form>}
            </aside>
          </div>
        </div>
      </section>

      {/* Content Section — How it works + Testimonials + Trust */}
      <section className="w-full bg-white border-t border-gray-200" data-id="element-1004">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-20" data-id="element-1005">
          {/* How It Works */}
          <div className="text-center mb-12" data-id="element-1006">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: 'rgba(232,89,79,0.1)',
            color: ORANGE
          }} data-id="element-1007">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: ORANGE
            }} data-id="element-1008"></span>
              HOW IT WORKS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1009">
              Your Path to the Right Program in 3 Steps
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1010">
              We've helped thousands of students find accredited online programs
              that fit their goals, schedule, and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-1011">
            {[{
            icon: ClipboardListIcon,
            step: '01',
            title: 'Tell Us About You',
            body: 'Share your goals, education level, and area of interest in under a minute.'
          }, {
            icon: SparklesIcon,
            step: '02',
            title: 'Get Matched Instantly',
            body: 'Our system pairs you with accredited online programs that fit your profile.'
          }, {
            icon: SchoolIcon,
            step: '03',
            title: 'Connect With Schools',
            body: 'Compare programs side-by-side and connect directly with admissions teams.'
          }].map((s, i) => {
            const Icon = s.icon;
            return <div key={i} className="relative bg-gray-50 rounded-2xl p-7 border border-gray-200 hover:border-[#e8594f]/40 hover:shadow-md transition-all" data-id="element-1012">
                  <div className="flex items-center justify-between mb-5" data-id="element-1013">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                  backgroundColor: 'rgba(232,89,79,0.1)'
                }} data-id="element-1014">
                      <Icon className="w-6 h-6" style={{
                    color: ORANGE
                  }} data-id="element-1015" />
                    </div>
                    <span className="text-3xl font-bold text-gray-200" data-id="element-1016">
                      {s.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1017">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1018">
                    {s.body}
                  </p>
                </div>;
          })}
          </div>

          {/* Testimonials */}
          {template.testimonials && template.testimonials.length > 0 && <div className="border-t border-gray-200 pt-16" data-id="element-1019">
              <div className="text-center mb-10" data-id="element-1020">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1021">
                  Trusted by Students Like You
                </h2>
                <div className="flex items-center justify-center gap-1.5 text-gray-600 text-sm" data-id="element-1022">
                  {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-4 h-4 fill-current" style={{
                color: ORANGE
              }} data-id="element-1023" />)}
                  <span className="ml-2" data-id="element-1024">
                    4.8 average rating from 70,000+ matched students
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-1025">
                {template.testimonials.map((t, i) => <div key={i} className="bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-200" data-id="element-1026">
                    <div className="flex gap-1 mb-3" data-id="element-1027">
                      {[1, 2, 3, 4, 5].map(s => <StarIcon key={s} className="w-4 h-4 fill-current" style={{
                  color: ORANGE
                }} data-id="element-1028" />)}
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-5 italic" data-id="element-1029">
                      "{t.quote}"
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-gray-200" data-id="element-1030">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{
                  backgroundColor: NAVY
                }} data-id="element-1031">
                        {t.initial}
                      </div>
                      <div data-id="element-1032">
                        <div className="font-semibold text-gray-900 text-sm" data-id="element-1033">
                          {t.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1034">{t.school}</div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* Trust band */}
          <div className="mt-16 rounded-2xl p-8 md:p-10 relative overflow-hidden" style={{
          backgroundColor: NAVY
        }} data-id="element-1035">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1036"></div>
            <div className="relative z-10 grid md:grid-cols-3 gap-6 items-center" data-id="element-1037">
              <div className="flex items-start gap-3" data-id="element-1038">
                <AwardIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1039" />
                <div data-id="element-1040">
                  <div className="font-bold text-white mb-1" data-id="element-1041">
                    Only Accredited Schools
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1042">
                    Every partner school is regionally or nationally accredited.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1043">
                <ShieldCheckIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1044" />
                <div data-id="element-1045">
                  <div className="font-bold text-white mb-1" data-id="element-1046">
                    Your Privacy Protected
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1047">
                    SSL encrypted. We never sell your data to unrelated third
                    parties.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1048">
                <ZapIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1049" />
                <div data-id="element-1050">
                  <div className="font-bold text-white mb-1" data-id="element-1051">
                    Always 100% Free
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1052">
                    No credit card, no fees — for you, ever.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full border-t border-gray-200 bg-white" data-id="element-1053">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-700" data-id="element-1054">
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4" data-id="element-1055">
            <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1056">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-1057">
              Terms of Use
            </a>
            <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-1058">
              CA Privacy Rights
            </a>
            <a href="/cookie-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1059">
              Cookies
            </a>
          </nav>
          <p className="text-xs text-gray-600 mb-1" data-id="element-1060">
            © {new Date().getFullYear()} FindOnlineDegrees.org · Media Matchers
            LLC · All Rights Reserved.
          </p>
          <p className="text-[11px] text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1061">
            FindOnlineDegrees.org is a free service that helps students explore
            accredited online degree programs. We may receive compensation from
            school partners when users inquire.
          </p>
        </div>
      </footer>
    </div>;
}